import { useState, useRef, useEffect, useId } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { getPricing } from "@/lib/api/pricing";

export const Route = createFileRoute("/spawn")({
  component: SpawnPage,
});

const NAME_REGEX = /^[a-zA-Z][a-zA-Z0-9-]{2,29}$/;

interface ApiAutomaton {
  name: string;
  [key: string]: unknown;
}

interface SpawnResponse {
  automaton: ApiAutomaton;
}

interface ErrorBody {
  error: string;
}

function SpawnPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [directive, setDirective] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const nameId = useId();
  const directiveId = useId();

  // Spawn cost is an admin-tunable pricing_config value; fetch it so the page
  // reflects the live DB value instead of a hardcoded number.
  const pricing = useQuery({ queryKey: ["pricing"], queryFn: getPricing });
  const spawnCost = pricing.data?.spawn_cost_c;

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const nameValid = NAME_REGEX.test(name);
  const directiveValid = directive.length >= 1 && directive.length <= 2000;

  async function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!nameValid || !directiveValid || submitting) return;
    setSubmitting(true);
    setError(null);

    const res = await api.post<SpawnResponse>("/v1/automatons", {
      name,
      directive,
    });

    if (res.ok && res.body?.automaton) {
      await navigate({
        to: "/a/$automatonName",
        params: { automatonName: res.body.automaton.name },
      });
      return;
    }

    setSubmitting(false);
    const code = (res.body as ErrorBody | null)?.error;
    if (code === "insufficient_funds") {
      setError("insufficient_funds");
    } else if (code === "name_taken") {
      setError("name_taken");
    } else if (code === "validation_failed") {
      setError("validation_failed");
    } else {
      setError("unknown");
    }
  }

  return (
    <div className="paper-page paper-page--onboarding">
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <p className="onb-eyebrow">SPAWN AUTOMATON</p>
        <h1 className="onb-headline">Configure and deploy</h1>
        <p className="onb-sub">
          Spawning costs {spawnCost ?? "…"}c, which becomes your automaton&apos;s starting energy.
        </p>

        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
        >
          <div style={{ marginBottom: 24 }}>
            <label className="onb-eyebrow" htmlFor={nameId}>
              NAME
            </label>
            <input
              id={nameId}
              ref={nameRef}
              className="onb-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="my-automaton"
              autoComplete="off"
              maxLength={30}
              aria-describedby={`${nameId}-hint`}
            />
            <p
              id={`${nameId}-hint`}
              className="onb-sub"
              style={{
                fontSize: 12,
                marginTop: 4,
                color: !name || nameValid ? "var(--color-fg-3)" : "var(--color-death)",
              }}
            >
              3–30 chars, starts with a letter, then letters/digits/hyphens.
            </p>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label className="onb-eyebrow" htmlFor={directiveId}>
              DIRECTIVE
            </label>
            <textarea
              id={directiveId}
              className="onb-input"
              value={directive}
              onChange={(e) => setDirective(e.target.value)}
              placeholder="Describe what this automaton should do…"
              rows={5}
              maxLength={2000}
              style={{ resize: "vertical", minHeight: 100, fontFamily: "var(--font-mono)" }}
              aria-describedby={`${directiveId}-count`}
            />
            <p
              id={`${directiveId}-count`}
              className="onb-sub"
              style={{ fontSize: 12, marginTop: 4, color: "var(--color-fg-3)", textAlign: "right" }}
            >
              {directive.length} / 2000
            </p>
          </div>

          {error && (
            <div
              role="alert"
              style={{
                marginBottom: 20,
                padding: "10px 14px",
                background: "var(--color-death-glow)",
                border: "1px solid var(--color-death-dim)",
                borderRadius: 4,
                fontSize: 13,
                color: "var(--color-death)",
              }}
            >
              {error === "insufficient_funds" && (
                <>
                  Insufficient wallet balance. You need at least {spawnCost ?? "…"}c.{" "}
                  <a href="/wallet" style={{ color: "var(--color-data)" }}>
                    Top up your wallet →
                  </a>
                </>
              )}
              {error === "name_taken" && "That name is already taken. Choose a different one."}
              {error === "validation_failed" && "Check name format and directive length."}
              {error === "unknown" && "Something went wrong. Try again."}
            </div>
          )}

          <div className="onb-actions onb-actions--end">
            <button
              type="submit"
              className="pg-btn pg-btn--accent"
              disabled={!nameValid || !directiveValid || submitting}
            >
              {submitting ? "Spawning…" : `Spawn · ${spawnCost ?? "…"}c`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
